import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import HomePage from './pages/HomePage';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Customers from './pages/Customers';
import Orders from './pages/Orders';
import Invoices from './pages/Invoices';
import DeliveryNotes from './pages/DeliveryNotes';
import RawMaterials from './pages/RawMaterials';
import ChalkCartons from './pages/ChalkCartons';
import SalesTargets from './pages/SalesTargets';
import BankTransactions from './pages/BankTransactions';
import Staff from './pages/Staff';
import Reports from './pages/Reports';
import ProductsManagement from './pages/ProductsManagement';
import QRCodeManagement from './pages/QRCodeManagement';
import ProductScan from './pages/ProductScan';
import Notifications from './pages/Notifications';
import StaffCustomerOrders from './pages/CustomerOrders';
import Quality from './pages/Quality';
import Pricing from './pages/Pricing';
import Finance from './pages/Finance';
import AuditLogs from './pages/AuditLogs';
import Website from './pages/Website';
import Settings from './pages/Settings';
import CustomerProfile from './pages/CustomerProfile';
import Layout from './components/Layout';

import CustomerRegister from './pages/customer/CustomerRegister';
import CustomerLogin from './pages/customer/CustomerLogin';
import CustomerProducts from './pages/customer/CustomerProducts';
import CustomerCart from './pages/customer/CustomerCart';
import CustomerCheckout from './pages/customer/CustomerCheckout';
import CustomerOrders from './pages/customer/CustomerOrders';

import { Toaster } from './components/ui/sonner';
import './App.css';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      setIsAuthenticated(true);
      setUser(JSON.parse(userData));
    }

    // Global 401 interceptor — auto-logout when staff token is invalid/expired
    const interceptor = axios.interceptors.response.use(
      (res) => res,
      (err) => {
        if (err?.response?.status === 401) {
          const path = window.location.pathname || '';
          const isStaffArea = !path.startsWith('/customer') && path !== '/login' && path !== '/' && !path.startsWith('/product/scan');
          // Only clear staff session for staff-area 401s (don't break customer auth)
          if (isStaffArea && localStorage.getItem('token')) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setIsAuthenticated(false);
            setUser(null);
            window.location.replace('/login?expired=1');
          }
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  const handleLogin = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setIsAuthenticated(true);
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    setUser(null);
  };

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          {/* Public Landing Page */}
          <Route path="/" element={<HomePage />} />
          
          {/* Public Product Scan Page */}
          <Route path="/product/scan/:qrId" element={<ProductScan />} />
          
          {/* Customer Portal Routes */}
          <Route path="/customer/register" element={<CustomerRegister />} />
          <Route path="/customer/login" element={<CustomerLogin />} />
          <Route path="/customer/products" element={<CustomerProducts />} />
          <Route path="/customer/cart" element={<CustomerCart />} />
          <Route path="/customer/checkout" element={<CustomerCheckout />} />
          <Route path="/customer/orders" element={<CustomerOrders />} />
          
          {/* Staff Login */}
          <Route
            path="/login"
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" />
              ) : (
                <Login onLogin={handleLogin} />
              )
            }
          />
          
          {/* Staff Portal Routes */}
          <Route
            path="/dashboard"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout}>
                  <Dashboard user={user} />
                </Layout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/customers"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout}>
                  <Customers user={user} />
                </Layout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/customers/:customerId"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout}>
                  <CustomerProfile user={user} />
                </Layout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/orders"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout}>
                  <Orders user={user} />
                </Layout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/invoices"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout}>
                  <Invoices user={user} />
                </Layout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/delivery-notes"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout}>
                  <DeliveryNotes user={user} />
                </Layout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/raw-materials"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout}>
                  <RawMaterials user={user} />
                </Layout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/chalk-cartons"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout}>
                  <ChalkCartons user={user} />
                </Layout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/sales-targets"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout}>
                  <SalesTargets user={user} />
                </Layout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/bank-transactions"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout}>
                  <BankTransactions user={user} />
                </Layout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/staff"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout}>
                  <Staff user={user} />
                </Layout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/products-management"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout}>
                  <ProductsManagement user={user} />
                </Layout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/website"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout}>
                  <Website user={user} />
                </Layout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/qr-codes"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout}>
                  <QRCodeManagement user={user} />
                </Layout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/notifications"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout}>
                  <Notifications user={user} />
                </Layout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/customer-orders"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout}>
                  <StaffCustomerOrders user={user} />
                </Layout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/settings"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout}>
                  <Settings user={user} />
                </Layout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/reports"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout}>
                  <Reports user={user} />
                </Layout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/pricing"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout}>
                  <Pricing user={user} />
                </Layout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/finance"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout}>
                  <Finance user={user} />
                </Layout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/quality"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout}>
                  <Quality user={user} />
                </Layout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/audit-logs"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout}>
                  <AuditLogs user={user} />
                </Layout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
